import type {
  Evaluation,
  EvaluationFindingReview,
  EvaluationReview,
} from '@compass/client/types/evaluation';
import type { FeedbackInfo } from '@compass/client/types/feedback';
import { FeedbackType } from '@compass/client/types/feedback';
import { type Scorecard, ScoringType } from '@compass/client/types/scorecard';

export interface FeedbackReviewFeedItem {
  actorUserId?: string;
  contextLabel: string;
  id: string;
  message: string;
  occurredAt: string;
}

type ScorecardQuestion = NonNullable<
  NonNullable<Scorecard['sections']>[number]['questions']
>[number];

const OVERALL_EVALUATION_CONTEXT = 'overall evaluation';
const NO_FEEDBACK_TEXT = 'No feedback provided.';
const HIDDEN_REVIEW_KEYS = new Set(['ae_pqc_detected']);

const reviewLabelOverrides: Record<string, string> = {
  compliance_failed: 'Compliance Failed',
  escalation: 'Escalation Required',
};

const acronymByToken: Record<string, string> = {
  ae: 'AE',
  hcp: 'HCP',
  hipaa: 'HIPAA',
  phi: 'PHI',
  pqc: 'PQC',
  qc: 'QC',
};

const normalizeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const formatToken = (token: string) => {
  if (acronymByToken[token]) {
    return acronymByToken[token];
  }

  return token.charAt(0).toUpperCase() + token.slice(1);
};

const formatReviewLabel = (key: string) => {
  const normalizedKey = normalizeKey(key);

  if (reviewLabelOverrides[normalizedKey]) {
    return reviewLabelOverrides[normalizedKey];
  }

  return normalizedKey.split('_').filter(Boolean).map(formatToken).join(' ');
};

const buildReviewMessage = (reviewKey: string, review: EvaluationReview) => {
  const label = formatReviewLabel(reviewKey);
  const reason = review.reason?.trim();

  if (review.accepted === false) {
    return reason ? `Dismissed ${label} — ${reason}` : `Dismissed ${label}`;
  }

  return `Confirmed ${label}`;
};

const getReviewerFindingAnswerLabel = (
  review: EvaluationFindingReview,
  scoringType?: ScorecardQuestion['scoring_type'],
) => {
  if (review.raw_score === undefined || review.raw_score === null) {
    return null;
  }

  if (scoringType === ScoringType.SCORING_TYPE_SCALE) {
    if (review.raw_score >= 1) return 'Yes';
    if (review.raw_score > 0) return 'Partial';
    return 'No';
  }

  return review.raw_score > 0 ? 'Yes' : 'No';
};

const getReviewerFindingEvidenceText = (review: EvaluationFindingReview) => {
  const snippet = review.evidence?.snippet?.trim();
  if (snippet) {
    return snippet;
  }

  const firstSnippet = review.evidence?.snippets?.find(item => item.text?.trim())?.text?.trim();
  return firstSnippet || null;
};

const buildFindingReviewMessage = (
  review: EvaluationFindingReview,
  question?: ScorecardQuestion,
) => {
  const answerLabel = getReviewerFindingAnswerLabel(review, question?.scoring_type);
  const evidenceText = getReviewerFindingEvidenceText(review);

  if (answerLabel && evidenceText) {
    return `Updated answer to ${answerLabel} — ${evidenceText}`;
  }
  if (answerLabel) {
    return `Updated answer to ${answerLabel}`;
  }
  if (evidenceText) {
    return `Updated evidence — ${evidenceText}`;
  }

  return 'Updated reviewer finding';
};

const getOccurredAtTimestamp = (occurredAt: string) => {
  const timestamp = Date.parse(occurredAt);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getQuestionDisplayTitle = (question: ScorecardQuestion) =>
  question.title?.trim() || question.label?.trim();

const collectQuestionTitlesByMetricKey = (scorecard?: Scorecard | null) => {
  const titleByMetricKey = new Map<string, string>();

  for (const section of scorecard?.sections || []) {
    for (const question of section.questions || []) {
      const metricKey = question.metric_key?.trim();
      const title = getQuestionDisplayTitle(question);

      if (metricKey && title) {
        titleByMetricKey.set(metricKey, title);
      }
    }
  }

  return titleByMetricKey;
};

const collectQuestionsByMetricKey = (scorecard?: Scorecard | null) => {
  const questionByMetricKey = new Map<string, ScorecardQuestion>();

  for (const section of scorecard?.sections || []) {
    for (const question of section.questions || []) {
      const metricKey = question.metric_key?.trim();
      if (metricKey && !questionByMetricKey.has(metricKey)) {
        questionByMetricKey.set(metricKey, question);
      }
    }
  }

  return questionByMetricKey;
};

const buildReviewItems = (
  evaluation: Evaluation,
  scorecard?: Scorecard | null,
): FeedbackReviewFeedItem[] => {
  const items: FeedbackReviewFeedItem[] = [];
  const reviews = evaluation.reviews;
  const questionByMetricKey = collectQuestionsByMetricKey(scorecard);
  const titleByMetricKey = collectQuestionTitlesByMetricKey(scorecard);

  if (!reviews) {
    return items;
  }

  const pushReview = (reviewKey: string, review: EvaluationReview | undefined) => {
    if (!review?.reviewed_at) {
      return;
    }

    items.push({
      actorUserId: review.reviewed_by,
      contextLabel: OVERALL_EVALUATION_CONTEXT,
      id: `review:${evaluation.id}:${reviewKey}:${review.reviewed_at}`,
      message: buildReviewMessage(reviewKey, review),
      occurredAt: review.reviewed_at,
    });
  };

  pushReview('escalation', reviews.escalation);
  pushReview('compliance_failed', reviews.compliance_failed);

  Object.entries(reviews.metadata || {}).forEach(([key, review]) => {
    if (HIDDEN_REVIEW_KEYS.has(normalizeKey(key))) {
      return;
    }
    pushReview(key, review);
  });

  Object.entries(reviews.findings || {}).forEach(([metricKey, review]) => {
    if (!review?.reviewed_at) {
      return;
    }

    const question = questionByMetricKey.get(metricKey);
    const contextLabel = titleByMetricKey.get(metricKey) || formatReviewLabel(metricKey);
    items.push({
      actorUserId: review.reviewed_by,
      contextLabel,
      id: `finding-review:${evaluation.id}:${metricKey}:${review.reviewed_at}`,
      message: buildFindingReviewMessage(review, question),
      occurredAt: review.reviewed_at,
    });
  });

  return items;
};

const getQuestionTitleByFindingIndex = (evaluation: Evaluation, scorecard?: Scorecard | null) => {
  const titleByMetricKey = collectQuestionTitlesByMetricKey(scorecard);
  const titleByFindingIndex = new Map<number, string>();

  for (const [index, finding] of (evaluation.findings || []).entries()) {
    const metricKey = finding.metric_key?.trim();
    const title = (metricKey && titleByMetricKey.get(metricKey)) || `Question ${index + 1}`;
    titleByFindingIndex.set(index, title);
  }

  return titleByFindingIndex;
};

const getFeedbackFindingIndex = (feedback: FeedbackInfo) => {
  if (feedback.feedback_type !== FeedbackType.FEEDBACK_TYPE_EVALUATION_FINDING) {
    return undefined;
  }

  if (feedback.finding_index === undefined || feedback.finding_index === null) {
    return undefined;
  }

  return feedback.finding_index;
};

const getFeedbackContextLabel = (
  findingIndex: number | undefined,
  questionTitleByFindingIndex: Map<number, string>,
) => {
  if (findingIndex === undefined) {
    return OVERALL_EVALUATION_CONTEXT;
  }

  return questionTitleByFindingIndex.get(findingIndex) || `Question ${findingIndex + 1}`;
};

const getFeedbackItemId = (
  feedback: FeedbackInfo,
  findingIndex: number | undefined,
  occurredAt: string,
  fallbackIndex: number,
) =>
  feedback.id ||
  `${feedback.evaluation_id || 'evaluation'}:${findingIndex ?? 'eval'}:${occurredAt}:${fallbackIndex}`;

const buildFeedbackItem = (
  feedback: FeedbackInfo,
  questionTitleByFindingIndex: Map<number, string>,
  fallbackIndex: number,
): FeedbackReviewFeedItem | null => {
  const occurredAt = feedback.created_at || feedback.updated_at;
  if (!occurredAt) {
    return null;
  }

  const findingIndex = getFeedbackFindingIndex(feedback);

  return {
    actorUserId: feedback.created_by,
    contextLabel: getFeedbackContextLabel(findingIndex, questionTitleByFindingIndex),
    id: `feedback:${getFeedbackItemId(feedback, findingIndex, occurredAt, fallbackIndex)}`,
    message: feedback.text?.trim() || NO_FEEDBACK_TEXT,
    occurredAt,
  };
};

const buildFeedbackItems = (
  evaluation: Evaluation,
  feedback: FeedbackInfo[] | undefined,
  scorecard?: Scorecard | null,
): FeedbackReviewFeedItem[] => {
  const questionTitleByFindingIndex = getQuestionTitleByFindingIndex(evaluation, scorecard);

  return (feedback || []).reduce<FeedbackReviewFeedItem[]>((items, item, index) => {
    const nextItem = buildFeedbackItem(item, questionTitleByFindingIndex, index);
    if (nextItem) {
      items.push(nextItem);
    }

    return items;
  }, []);
};

export const getFeedbackReviewItems = (
  evaluation: Evaluation,
  feedback: FeedbackInfo[] | undefined,
  scorecard?: Scorecard | null,
): FeedbackReviewFeedItem[] =>
  [
    ...buildReviewItems(evaluation, scorecard),
    ...buildFeedbackItems(evaluation, feedback, scorecard),
  ].sort((left, right) => {
    const leftTime = getOccurredAtTimestamp(left.occurredAt);
    const rightTime = getOccurredAtTimestamp(right.occurredAt);

    if (leftTime === null || rightTime === null) {
      return right.occurredAt.localeCompare(left.occurredAt);
    }

    return rightTime - leftTime;
  });
